import { useRef, useState } from 'react';

import { useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

import {
  StyledMenuItem,
  StyledText,
} from '@webclient/components/settings/components/tabs/voice/components/styles';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';

import { setVoiceTypeByUserId } from '@webclient/state/slices/app-settings';
import { selectVoice } from '@webclient/state/slices/app-settings/userSettingSelector';

import { VoiceType } from '@webclient/shared-types/settings';

import CheckIcon from '~icons/knowz-iconify/check-rounded';
import StopRoundedIcon from '~icons/knowz-iconify/stop-alt2-rounded';
import VolumeMaxRoundedIcon from '~icons/knowz-iconify/volume-max-rounded';

import { IconButton, Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';

type LabelsType =
  | 'Alloy'
  | 'Ash'
  | 'Ballad'
  | 'Coral'
  | 'Echo'
  | 'Sage'
  | 'Shimmer'
  | 'Verse'
  | 'Nova';

const BASE_URL = 'https://asset.knowz.com/assets/medias/voices';

const VOICES: Record<VoiceType, { label: LabelsType; previewUrl: string }> = {
  nova: {
    label: 'Nova',
    previewUrl: `${BASE_URL}/nova.wav`,
  },
  alloy: {
    label: 'Alloy',
    previewUrl: `${BASE_URL}/alloy.wav`,
  },
  ash: {
    label: 'Ash',
    previewUrl: `${BASE_URL}/ash.wav`,
  },
  ballad: {
    label: 'Ballad',
    previewUrl: `${BASE_URL}/ballad.wav`,
  },
  coral: {
    label: 'Coral',
    previewUrl: `${BASE_URL}/coral.wav`,
  },
  echo: {
    label: 'Echo',
    previewUrl: `${BASE_URL}/echo.wav`,
  },
  sage: {
    label: 'Sage',
    previewUrl: `${BASE_URL}/sage.wav`,
  },
  shimmer: {
    label: 'Shimmer',
    previewUrl: `${BASE_URL}/shimmer.wav`,
  },
  verse: {
    label: 'Verse',
    previewUrl: `${BASE_URL}/verse.wav`,
  },
} as const;

export default function SpeakerSelector() {
  const me = useAppSelector((state) => state.auth.me);
  const dispatch = useAppDispatch();
  const voice = useAppSelector(selectVoice);
  const theme = useTheme();
  const { t } = useTranslate();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  function handleVoiceOnchage(event: SelectChangeEvent) {
    if (!me) return;
    dispatch(
      setVoiceTypeByUserId({
        type: event.target.value as VoiceType,
        userId: me.uuid,
      }),
    );
    handleTogglePlayStop('stop')();
  }

  function handleStopAudio() {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
      audio.currentTime = 0;
    }
  }
  function handlePlayAudio() {
    const audio = audioRef.current;
    if (audio) {
      audio.src = VOICES[voice.type].previewUrl;
      audio.play().catch((error) => {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
      });
    }
  }

  function handleTogglePlayStop(type: 'play' | 'stop') {
    return () => {
      if (type === 'stop') {
        handleStopAudio();
        setIsPlaying(false);
        return;
      }
      handlePlayAudio();
      setIsPlaying(true);
    };
  }

  return (
    <Stack
      direction="row"
      gap={2}
      sx={{
        alignItems: 'center',
      }}
    >
      <audio
        ref={audioRef}
        controls={false}
        onEnded={() => {
          handleStopAudio();
          setIsPlaying(false);
        }}
      />
      <Tooltip
        title={
          isPlaying
            ? t(
                'layout.main.sections.header.settings.voice.speaker.stop',
                'Stop',
              )
            : t(
                'layout.main.sections.header.settings.voice.speaker.play',
                'Play',
              )
        }
        placement="top"
        arrow
      >
        <IconButton
          onClick={handleTogglePlayStop(isPlaying ? 'stop' : 'play')}
          size="small"
          color="secondary"
          variant="ghost"
        >
          {isPlaying ? <StopRoundedIcon /> : <VolumeMaxRoundedIcon />}
        </IconButton>
      </Tooltip>
      <Select
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                p: 0,
              },
            },
          },
        }}
        value={voice.type}
        onChange={handleVoiceOnchage}
        variant="outlined"
        renderValue={(voiceKind) => {
          const { label } = VOICES[voiceKind];

          return (
            <Stack
              key={voiceKind}
              direction="row"
              gap={2}
            >
              <Text variant="textSm">{label}</Text>
            </Stack>
          );
        }}
      >
        <StyledText
          variant="textMd"
          color="text.secondary"
          weight="semibold"
        >
          {t(
            'layout.main.sections.header.settings.voice.speaker.title',
            'Knowz Voice',
          )}
        </StyledText>
        <Divider sx={{ marginBlock: 1 }} />
        {Object.entries(VOICES).map(
          ([key, value]: [string, { label: LabelsType }]) => {
            const voiceKind = key as VoiceType;
            const { label } = value;
            return (
              <StyledMenuItem
                key={voiceKind}
                value={voiceKind}
              >
                <Text variant="textSm">{label}</Text>
                {voice.type === voiceKind && (
                  <CheckIcon color={theme.palette.border.brand} />
                )}
              </StyledMenuItem>
            );
          },
        )}
      </Select>
    </Stack>
  );
}
